import { useMutation, useQuery } from '@tanstack/react-query'

import { convertObjectToCamelCase, convertObjectToSnakeCase } from '@/helpers'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'

import type { SaleOrderItemProps } from '@/types'

import { SALE_ORDER_ITEM_URL } from './api.urls'

export const useSaleOrderItemDetail = (uuid?: string) => {
  const getOrderDetail = async (): Promise<{
    success: boolean
    message: string
    data?: SaleOrderItemProps
  }> => {
    try {
      const { data } = await api.get(SALE_ORDER_ITEM_URL(`${uuid}`))

      return {
        success: true,
        message: 'Order request fetched successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderItemProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get order request!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ORDER_ITEM.module, uuid],
    queryFn: () => getOrderDetail(),
    enabled: !!uuid,
  })
}

export const useSaleOrderItemUpdate = (uuid?: string) => {
  const updateOrderItem = async (
    payload: Partial<SaleOrderItemProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: SaleOrderItemProps
  }> => {
    try {
      const { data } = await api.patch(
        SALE_ORDER_ITEM_URL(`${uuid || payload?.uuid}`),
        convertObjectToSnakeCase({ ...payload, uuid: undefined }),
      )

      return {
        success: true,
        message: 'Order item updated successfully!',
        data: convertObjectToCamelCase(data) as SaleOrderItemProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to update order item!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.ORDER.module, uuid],
    mutationFn: (payload: Partial<SaleOrderItemProps>) =>
      updateOrderItem(payload),
  })
}
